import * as ort from "onnxruntime-web";
import { AudioFeatures, CHUNK } from "./audio-features";

const DEFAULT_INPUT_FRAMES = 16;

export type DetectionEvent = {
  label: string;
  score: number;
};

export type WakeWordOptions = {
  baseUrl?: string;
  /**
   * Built-in model ids (`hey_jarvis`) and/or onnx filenames / absolute URLs.
   * Filenames resolve under `baseUrl`; absolute paths/URLs are used as-is.
   */
  wakewordModels?: string[];
  threshold?: number;
  onDetection?: (event: DetectionEvent) => void;
  wasmPaths?: string;
};

type ModelEntry = {
  session: ort.InferenceSession;
  inputName: string;
  inputFrames: number;
  outputClasses: number;
};

const MODEL_FILES: Record<string, string> = {
  alexa: "alexa_v0.1.onnx",
  hey_mycroft: "hey_mycroft_v0.1.onnx",
  hey_jarvis: "hey_jarvis_v0.1.onnx",
};

function modelKey(name: string): string {
  // Absolute /models/.../foo.onnx → use basename without extension as label key
  const file = name.split("/").pop() ?? name;
  return file.replace(/\.onnx$/i, "") || name;
}

function configureOrt(wasmPaths?: string) {
  // Self-hosted under /public/ort (npm run download-models).
  // Trailing slash is required so ORT resolves sibling .mjs/.wasm files.
  const paths = wasmPaths ?? "/ort/";
  ort.env.wasm.wasmPaths = paths.endsWith("/") ? paths : `${paths}/`;
  // Single-threaded avoids needing COOP/COEP + SharedArrayBuffer.
  ort.env.wasm.numThreads = 1;
  ort.env.wasm.proxy = false;
}

/**
 * On-device wake word detector (openWakeWord pipeline via ONNX Runtime Web).
 */
export class OpenWakeWord {
  private models: Record<string, ModelEntry> = {};
  private features: AudioFeatures | null = null;
  private predictionBuffer: Record<string, number[]> = {};
  /** Rising-edge latch so we only fire once per wake, not every frame above threshold */
  private armed: Record<string, boolean> = {};
  threshold = 0.95;
  onDetection: ((event: DetectionEvent) => void) | null = null;

  static async create(opts: WakeWordOptions = {}): Promise<OpenWakeWord> {
    const {
      baseUrl = "/models/",
      wakewordModels,
      threshold = 0.95,
      onDetection = null,
      wasmPaths = "/ort/",
    } = opts;

    if (!wakewordModels?.length) {
      throw new Error("wakewordModels is required.");
    }

    configureOrt(wasmPaths);

    const join = (file: string) =>
      /^https?:|^\.|^\//.test(file) ? file : baseUrl + file;

    const self = new OpenWakeWord();
    const sessOpts = { executionProviders: ["wasm"] as const };

    const [melspecSession, embeddingSession] = await Promise.all([
      ort.InferenceSession.create(join("melspectrogram.onnx"), sessOpts),
      ort.InferenceSession.create(join("embedding_model.onnx"), sessOpts),
    ]);
    self.features = new AudioFeatures(melspecSession, embeddingSession);

    for (const name of wakewordModels) {
      const file = MODEL_FILES[name] ?? name;
      const session = await ort.InferenceSession.create(join(file), sessOpts);
      const key = MODEL_FILES[name] ? name : modelKey(name);
      self.models[key] = {
        session,
        inputName: session.inputNames[0],
        inputFrames: DEFAULT_INPUT_FRAMES,
        outputClasses: 1,
      };
    }

    self.threshold = threshold;
    self.onDetection = onDetection;
    await self.features.warmup();
    return self;
  }

  get modelNames() {
    return Object.keys(this.models);
  }

  async reset() {
    if (!this.features) return;
    this.features.reset(true);
    await this.features.warmup();
    this.predictionBuffer = {};
    this.armed = {};
  }

  private async runModel(m: ModelEntry, feat: { data: Float32Array; dims: number[] }) {
    const tensor = new ort.Tensor("float32", feat.data, feat.dims);
    const out = await m.session.run({ [m.inputName]: tensor });
    const data = out[m.session.outputNames[0]].data as Float32Array;
    return Array.from(data);
  }

  private pushPrediction(label: string, value: number) {
    if (!this.predictionBuffer[label]) this.predictionBuffer[label] = [];
    this.predictionBuffer[label].push(value);
    if (this.predictionBuffer[label].length > 30) {
      this.predictionBuffer[label].shift();
    }
  }

  async predict(x: Int16Array): Promise<Record<string, number>> {
    if (!(x instanceof Int16Array)) {
      throw new TypeError("Input audio must be an Int16Array of 16 kHz PCM.");
    }
    if (!this.features) {
      throw new Error("OpenWakeWord is not initialised.");
    }

    const nPrepared = await this.features.streamingFeatures(x);
    const predictions: Record<string, number> = {};

    for (const [name, m] of Object.entries(this.models)) {
      let prediction: number[];

      if (nPrepared > CHUNK) {
        const group: number[][] = [];
        for (let i = Math.floor(nPrepared / CHUNK) - 1; i >= 0; i--) {
          const feat = this.features.getFeatures(
            m.inputFrames,
            -m.inputFrames - i,
          );
          group.push(await this.runModel(m, feat));
        }
        prediction = group.reduce((acc, row) =>
          acc.map((v, idx) => Math.max(v, row[idx])),
        );
      } else if (nPrepared === CHUNK) {
        const feat = this.features.getFeatures(m.inputFrames);
        prediction = await this.runModel(m, feat);
      } else {
        const buf = this.predictionBuffer[name];
        prediction = [buf && buf.length > 0 ? buf[buf.length - 1] : 0];
      }

      predictions[name] = prediction[0] ?? 0;
    }

    for (const label of Object.keys(predictions)) {
      if (
        !this.predictionBuffer[label] ||
        this.predictionBuffer[label].length < 5
      ) {
        predictions[label] = 0;
      }
    }

    for (const [label, score] of Object.entries(predictions)) {
      this.pushPrediction(label, score);
    }

    if (this.onDetection) {
      const release = this.threshold * 0.55;
      for (const [label, score] of Object.entries(predictions)) {
        if (score < release) {
          this.armed[label] = true;
        }
        if (score >= this.threshold && this.armed[label] !== false) {
          // Fire once, then stay disarmed until score drops below release.
          this.armed[label] = false;
          this.onDetection({ label, score });
        }
      }
    }

    return predictions;
  }
}
